import { BodyContent } from 'components/BodyContent/BodyContent';
import React, { useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { PageContext } from 'src/context/PageContext';
import { SharePage } from 'components/Content/ContentTypes/SharePage/SharePage';
import { getSectionTabs } from 'src/utils/getSectionTabs';
import { getSectionNavigationMode } from 'src/utils/getSectionNavigationMode';
import { hasHeroSection } from 'src/utils/heroSection';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import styles from './ContentPage.module.scss';
import { groupSectionsByTab } from './groupSectionsByTab';
import { SectionTabGroup } from './SectionTabGroup';

export function ContentPage() {
  const { page } = useContext(PageContext);
  const { translate, translateDefault } = useGetContentTranslations();
  const router = useRouter();
  const sectionNavigationMode = getSectionNavigationMode(page?.sections ?? []);
  const { tabs, ownerSlugBySectionId } = useMemo(
    () => getSectionTabs(page?.sections ?? [], translate, translateDefault),
    [page?.sections, translate, translateDefault],
  );
  const contentTabs = useMemo(() => tabs.filter((tab) => !tab.isHero), [tabs]);
  const isTabsModeActive = sectionNavigationMode === 'tabbed_content' && contentTabs.length > 1;
  const queryParam = router.query.section;
  const requestedSlug = Array.isArray(queryParam) ? queryParam[0] : queryParam;
  const activeSectionSlug =
    requestedSlug && contentTabs.some((tab) => tab.slug === requestedSlug) ? requestedSlug : contentTabs[0]?.slug;

  const sectionGroups = useMemo(
    () => groupSectionsByTab(page?.sections ?? [], ownerSlugBySectionId),
    [page?.sections, ownerSlugBySectionId],
  );
  const indexBySectionId = useMemo(
    () => Object.fromEntries((page?.sections ?? []).map((entry, position) => [entry.id, position])),
    [page?.sections],
  );

  return (
    <BodyContent>
      <main className={styles.main}>
        {sectionGroups.map((group) => (
          <SectionTabGroup
            key={group.sections[0].id}
            sections={group.sections}
            allSections={page?.sections ?? []}
            firstIndex={indexBySectionId[group.sections[0].id]}
            navigationMode={isTabsModeActive ? 'tabs' : 'anchor'}
            isHero={hasHeroSection(group.sections)}
            hasOwningTab={!!group.ownerSlug}
            isHidden={isTabsModeActive && !!group.ownerSlug && group.ownerSlug !== activeSectionSlug}
          />
        ))}
      </main>
      {page?.metadata?.social_sharing_enabled && <SharePage />}
    </BodyContent>
  );
}
